// Creator sign-in. Google OAuth is the primary path — it works inside an
// installed PWA and avoids email rate limits / template config. The email
// magic-link + 6-digit code flow stays as a backup for anyone who'd rather
// not use Google. Caretakers never see this (they join via an invite link).

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { useT } from "../lib/LocaleContext";

export function Login() {
  const { signInWithGoogle, signInWithEmail, verifyEmailOtp } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [googling, setGoogling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogle() {
    setGoogling(true);
    setError(null);
    const { error: err } = await signInWithGoogle();
    if (err) {
      setError(err);
      setGoogling(false);
    }
    // On success the browser redirects to Google, so we never reach here.
  }

  async function send() {
    setSubmitting(true);
    setError(null);
    const { error: err } = await signInWithEmail(email.trim());
    setSubmitting(false);
    if (err) setError(err);
    else setSent(true);
  }

  function handleSend(e: FormEvent) {
    e.preventDefault();
    void send();
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setVerifying(true);
    setError(null);
    const { error: err } = await verifyEmailOtp(email.trim(), code.trim());
    setVerifying(false);
    if (err) setError(t.login.codeError);
    else navigate("/", { replace: true });
  }

  if (sent) {
    return (
      <main className="screen screen--center">
        <header className="hero">
          <div className="hero__emoji" aria-hidden="true">✉️</div>
          <h1>{t.login.sentTitle}</h1>
          <p className="hero__sub">{t.login.sentBody(email)}</p>
        </header>

        <form className="form" onSubmit={handleVerify}>
          <label className="field">
            <span className="field__label">{t.login.codeLabel}</span>
            <input
              className="field__input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder={t.login.codePlaceholder}
            />
            <span className="field__hint">{t.login.codeHint}</span>
          </label>

          {error && <p className="error" role="alert">{error}</p>}

          <button
            className="btn btn--primary btn--block"
            type="submit"
            disabled={verifying || code.length < 6}
          >
            {verifying ? t.login.verifying : t.login.verify}
          </button>
        </form>

        <button
          className="btn btn--ghost btn--block"
          type="button"
          disabled={submitting}
          onClick={() => void send()}
        >
          {submitting ? t.login.submitting : t.login.resend}
        </button>
      </main>
    );
  }

  return (
    <main className="screen screen--center">
      <header className="hero">
        <div className="hero__emoji" aria-hidden="true">👶</div>
        <h1>{t.login.title}</h1>
        <p className="hero__sub">{t.login.subtitle}</p>
      </header>

      <button
        className="btn btn--primary btn--block"
        type="button"
        disabled={googling}
        onClick={() => void handleGoogle()}
      >
        {googling ? t.login.googleSubmitting : t.login.google}
      </button>

      <div className="login__divider" aria-hidden="true">
        <span>{t.login.or}</span>
      </div>

      <form className="form" onSubmit={handleSend}>
        <label className="field">
          <span className="field__label">{t.login.emailLabel}</span>
          <input
            className="field__input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.login.emailPlaceholder}
            autoCapitalize="off"
          />
          <span className="field__hint">{t.login.emailHint}</span>
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <button
          className="btn btn--ghost btn--block"
          type="submit"
          disabled={submitting || !email.trim()}
        >
          {submitting ? t.login.submitting : t.login.submit}
        </button>
      </form>
    </main>
  );
}
